import {
  AdminSpinner,
  MenuNamesResponse,
  ProviderConnectionStatus,
  getLocationPosStatuses,
  reportError,
} from '@vgrubs/components';
import {
  PosStatusResponse,
  PosType,
} from 'components/src/types/api/location.types';
import { useEffect, useState } from 'react';
import { PosComponent } from './PosComponent';
import useManageProvidersSocket from '../ManageProvidersSocket';

export const ManagePOS = ({
  locationId,
  brandId,
  pathPrefix,
  onOpenPlaybook,
}: {
  locationId: string;
  brandId: string;
  storeName: MenuNamesResponse | null;
  pathPrefix: 'locations' | 'brands';
  /** Opens the integration playbook; offered from a connected POS's details. */
  onOpenPlaybook?: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const isLocations = pathPrefix === 'locations';
  const id = isLocations ? locationId : brandId;

  const [statuses, setStatuses] = useState<PosStatusResponse>([]);

  useManageProvidersSocket({
    brandId: isLocations ? undefined : Number(brandId),
    locationId: isLocations ? Number(locationId) : undefined,
    getProvidersStatuses,
  });

  async function getProvidersStatuses(silent?: boolean) {
    try {
      if (!silent) setLoading(true);
      // Add cache-busting to prevent browser from serving cached responses
      const statusesData = await getLocationPosStatuses(id, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
        params: {
          _t: Date.now(), // Timestamp to bypass cache
        },
      });

      setStatuses(statusesData.data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getProvidersStatuses();
    return () => {
      setStatuses([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enabledProviders = [PosType.clover, PosType.toast, PosType.lightspeedO];

  return (
    <>
      {loading && <AdminSpinner />}

      {Object.values(PosType).map((p, i) => {
        const status = statuses.find((s) => s.type === p);
        return (
          <PosComponent
            key={i}
            provider={p}
            id={id}
            status={status}
            pathPrefix={pathPrefix}
            getProvidersStatuses={getProvidersStatuses}
            disabled={!enabledProviders.includes(p)}
            disabledBecauseOfOtherPosConnected={statuses.some(
              (s) =>
                s.connectionStatus === ProviderConnectionStatus.connected &&
                s.type !== p
            )}
            onOpenPlaybook={onOpenPlaybook}
          />
        );
      })}
    </>
  );
};
